/**
 * AiCreateModal - "Create from screenshot / text" (Paul Sarantos, 10/6/26:
 * "manually entering this info is cumbersome").
 *
 * Step 1: paste or type text, and/or drop / pick / paste (Ctrl+V) screenshots
 * of an email, a signature, a business card, an RFQ (PDFs too). "Read it"
 * sends them to POST /api/ai/extract, which writes nothing.
 *
 * Step 2: one card per thing found - Account, Contacts, Opportunity,
 * Activity - each editable, each with its own button into the EXISTING create
 * flow, so every rule those forms have still applies and nothing is saved
 * until the person presses that form's Save:
 *   Account      an existing match -> Use existing; new -> AccountForm, prefilled
 *                (stage, lead source and the first task are still theirs to fill)
 *   Contacts     Add contact -> POST /api/accounts/:id/contacts (the contact
 *                form's fields and payload)
 *   Opportunity  EditOpportunityModal as a NEW opportunity, prefilled and linked
 *   Activity     ActivityForm on the account, prefilled, the screenshots staged
 * The account comes first: the other cards wait until one is chosen or made.
 * Each card only offers what the person has Edit for (`rights`, aiCreateModel).
 *
 * A failure (assistant not set up, nothing found, an error) keeps everything
 * pasted, so the person can try again or type it in.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useApp } from '../../context/AppContext';
import { acceptDrag, filesOf } from '../../utils/droppedFiles';
import AccountForm from '../forms/AccountForm';
import ActivityForm from '../crm/ActivityForm';
import AccountSitePicker from '../crm/AccountSitePicker';
import { ContactFields } from '../crm/ContactDetailPanel';
import { contactPayload } from '../crm/crmFormHelpers';
import { INPUT, LABEL } from '../crm/crmStyles';
import { EditOpportunityModal } from '../opportunities/EditOpportunityModal';
import { MEETING_TYPES } from '../../data/crmStages';
import {
  MAX_FILES,
  MAX_TOTAL_MB,
  ACCEPT,
  readable,
  accountPrefill,
  contactForm,
  companyHasSites,
  opportunitySeed,
  activityPrefill,
  accountLabel,
  REASON_LABEL,
  MODES,
} from './aiCreateModel';

const objectUrl = (file) =>
  file.type.startsWith('image/') && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
const dropUrl = (u) => {
  if (u && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(u);
};

const CARD = 'border border-border rounded-lg p-3.5 space-y-2.5';
const CHIP = {
  matched: 'bg-gat-green/10 text-gat-green',
  similar: 'bg-amber-100 text-amber-800',
  new: 'bg-black/5 text-text-body',
  done: 'bg-gat-green text-white',
};

function Chip({ tone, children }) {
  return <span className={clsx('ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide', CHIP[tone])}>{children}</span>;
}

function Field({ label, value, onChange, type = 'text', area = false, wide = false }) {
  return (
    <label className={clsx('block', wide && 'col-span-2')}>
      <span style={LABEL}>{label}</span>
      {area ? (
        <textarea style={{ ...INPUT, minHeight: 64, resize: 'vertical' }} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} style={INPUT} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

const WAIT_FOR_ACCOUNT = 'Choose or create the account first.';

export default function AiCreateModal({ onClose, rights, mode = 'accounts' }) {
  const { allAccounts = [], fetchAll } = useApp();
  const M = MODES[mode];
  // the cards numbered in the order shown (only this tab's own cards)
  const order = ['account', ...['contacts', 'opportunity', 'activity'].filter((c) => M.cards[c])];
  const num = (card) => order.indexOf(card) + 1;

  /* ── step 1: what to read ── */
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]); // [{ file, url }]
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone: 'err' | 'info', text }
  const pick = useRef(null);
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => () => filesRef.current.forEach((f) => dropUrl(f.url)), []);

  /* ── step 2: what was found ── */
  const [found, setFound] = useState(null); // { extraction, matches }
  const [acct, setAcct] = useState(accountPrefill(null));
  const [accountId, setAccountId] = useState('');
  const [createdRow, setCreatedRow] = useState(null); // the account made here, until the store has it
  const [people, setPeople] = useState([]); // [{ form, match, added, err, busy }]
  const [opp, setOpp] = useState(null);
  const [act, setAct] = useState(null);
  const [attach, setAttach] = useState(true); // the screenshots go on the activity
  const [done, setDone] = useState({}); // { opportunity, activity }
  const [sub, setSub] = useState(null); // 'account' | 'activity' | 'opportunity' - a create form is open

  const add = useCallback((list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) return;
    const bad = incoming.filter((f) => !readable(f));
    const good = incoming.filter(readable);
    setFiles((cur) => {
      const next = [...cur, ...good.map((file) => ({ file, url: objectUrl(file) }))];
      next.slice(MAX_FILES).forEach((f) => dropUrl(f.url));
      if (bad.length) setMsg({ tone: 'err', text: `Only screenshots, photos and PDFs can be read: ${bad.map((f) => f.name).join(', ')}` });
      else if (next.length > MAX_FILES) setMsg({ tone: 'err', text: `At most ${MAX_FILES} files - extra ones were left off.` });
      else setMsg(null);
      return next.slice(0, MAX_FILES);
    });
  }, []);

  // Ctrl+V of an image (Win+Shift+S, then paste) adds it; pasted text goes in the box as usual.
  useEffect(() => {
    if (found) return undefined;
    const onPaste = (e) => {
      const imgs = filesOf(e.clipboardData, { renameGeneric: true });
      if (imgs.length) {
        e.preventDefault();
        add(imgs);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [found, add]);

  const totalMb = files.reduce((n, f) => n + f.file.size, 0) / 1024 / 1024;
  const canRead = !busy && (text.trim() || files.length) && totalMb <= MAX_TOTAL_MB;

  const read = async () => {
    setBusy(true);
    setMsg(null);
    const fd = new FormData();
    fd.append('text', text);
    files.forEach((f) => fd.append('files', f.file));
    try {
      const r = await fetch('/api/ai/extract', { method: 'POST', body: fd });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Could not read it - try again.');
      const ex = body.extraction || {};
      if (!ex.found) {
        setMsg({ tone: 'info', text: 'Nothing to create was found in that. Try a clearer screenshot, or paste the text itself.' });
        return;
      }
      const m = body.matches || { account: { status: 'new', candidates: [] }, contacts: [] };
      setFound({ extraction: ex, matches: m, matchFailed: !body.matches });
      setAcct(accountPrefill(ex.account));
      setAccountId('');
      setCreatedRow(null);
      setPeople(
        (ex.contacts || []).map((c, i) => ({
          form: contactForm(c),
          match: (m.contacts || [])[i] || null,
          added: null,
          err: '',
          busy: false,
        }))
      );
      setOpp(
        ex.opportunity
          ? { ...ex.opportunity }
          : M.cards.opportunity
            ? { topic: '', description: '', customerReference: '', dueDate: '', value: '', notes: '' }
            : null
      );
      setAct(ex.activity ? { ...activityPrefill(ex.activity) } : null);
      setDone({});
    } catch (e) {
      setMsg({ tone: 'err', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const account = useMemo(() => {
    if (!accountId) return null;
    const live = allAccounts.find((a) => a.id === accountId);
    if (live) return live;
    return createdRow && createdRow.id === accountId
      ? { id: createdRow.id, name: createdRow.name, parentAccountId: createdRow.parent_account_id || null }
      : null;
  }, [accountId, allAccounts, createdRow]);
  const accountName = account
    ? accountLabel({
        name: account.name,
        parent: account.parentAccountId ? (allAccounts.find((a) => a.id === account.parentAccountId) || {}).name : null,
      })
    : '';

  const addContact = async (i) => {
    if (!account) return;
    const p = people[i];
    if (!p.form.name.trim()) {
      setPerson(i, { err: "Enter the contact's name." });
      return;
    }
    setPerson(i, { busy: true, err: '' });
    try {
      const r = await fetch(`/api/accounts/${encodeURIComponent(account.id)}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactPayload(p.form)),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setPerson(i, { busy: false, err: b.error || 'Could not add the contact - try again.' });
        return;
      }
      setPerson(i, { busy: false, added: account.name });
      if (fetchAll) fetchAll();
    } catch {
      setPerson(i, { busy: false, err: 'Could not add the contact - try again.' });
    }
  };
  function setPerson(i, patch) {
    setPeople((cur) => cur.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  }

  // Escape / the close box backs out of an open create form first, never past it
  const close = () => {
    if (sub) {
      setSub(null);
      return;
    }
    if (!busy) onClose();
  };

  const ex = found && found.extraction;
  const am = found && found.matches.account;

  /* ── the create forms, in place of the review ── */
  let body;
  if (found && sub === 'account') {
    body = (
      <div>
        <div className="text-[12px] text-text-body mb-2">Check the details, then pick the stage, lead source and first task.</div>
        <AccountForm
          prefill={acct}
          // made to attach an opportunity: an Account, not a lead (Paul, 10/21/26)
          defaultDesignation={mode === 'opportunities' ? 'Account' : 'Lead'}
          onSaved={(row) => {
            if (row && row.id) {
              setCreatedRow(row);
              setAccountId(row.id);
            }
          }}
          onClose={() => setSub(null)}
        />
      </div>
    );
  } else if (found && sub === 'activity' && account) {
    body = (
      <div>
        <div className="text-[12px] text-text-body mb-2">
          Logging on <b>{accountName}</b>.
        </div>
        <ActivityForm
          record={account}
          defaults={{ contact_ids: [], opportunity_ids: [] }}
          companyWithSites={companyHasSites(account, allAccounts)}
          prefill={act}
          initialFiles={attach ? files.map((f) => f.file) : []}
          onSaved={() => {
            setDone((d) => ({ ...d, activity: true }));
            setSub(null);
            if (fetchAll) fetchAll();
          }}
          onCancel={() => setSub(null)}
        />
      </div>
    );
  } else if (found) {
    body = (
      <div className="space-y-3 text-[12px]">
        {found.matchFailed && (
          <div className="text-status-lost">Could not check for existing accounts - look before creating a new one.</div>
        )}
        {ex.notes && <div className="text-text-muted italic">Note from the assistant: {ex.notes}</div>}

        {/* ACCOUNT - first; the others wait for it */}
        <section className={CARD} aria-label="Account">
          <div className="flex items-center">
            <span className="font-bold text-text-dark">1. Account</span>
            {account ? (
              <Chip tone="done">Chosen</Chip>
            ) : am.status === 'matched' ? (
              <Chip tone="matched">Existing</Chip>
            ) : am.status === 'similar' ? (
              <Chip tone="similar">Possible match</Chip>
            ) : (
              <Chip tone="new">New</Chip>
            )}
          </div>
          {account ? (
            <div className="flex items-center gap-2">
              <span>
                Using <b>{accountName}</b>
                {createdRow && createdRow.id === account.id ? ' (just created)' : ''}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setAccountId('')}>
                Change
              </Button>
            </div>
          ) : (
            <>
              {am.candidates.length > 0 && (
                <ul className="space-y-1.5">
                  {am.candidates.map((c) => (
                    <li key={c.id} className="flex items-center gap-2">
                      <span className="flex-1">
                        <span className="text-text-muted">{c.reason === 'similar' ? 'Possible match: ' : 'Existing account: '}</span>
                        <b>{accountLabel(c)}</b> <span className="text-text-muted">({REASON_LABEL[c.reason] || c.reason})</span>
                      </span>
                      <Button size="sm" variant={c.reason === 'similar' ? 'secondary' : 'primary'} onClick={() => setAccountId(c.id)}>
                        Use existing
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Company name" value={acct.companyName} onChange={(v) => setAcct((a) => ({ ...a, companyName: v }))} wide />
                <Field label="Industry" value={acct.industry} onChange={(v) => setAcct((a) => ({ ...a, industry: v }))} />
                <Field label="Phone" value={acct.phone} onChange={(v) => setAcct((a) => ({ ...a, phone: v }))} />
                <Field label="Website" value={acct.website} onChange={(v) => setAcct((a) => ({ ...a, website: v }))} />
                <Field label="Location / HQ" value={acct.location} onChange={(v) => setAcct((a) => ({ ...a, location: v }))} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {rights.account ? (
                  <Button size="sm" variant={am.status === 'matched' ? 'secondary' : 'primary'} onClick={() => setSub('account')}>
                    {am.status === 'new' ? 'Create account' : 'Create a new account instead'}
                  </Button>
                ) : (
                  <span className="text-text-muted">Creating an account needs Edit on Accounts - use an existing one.</span>
                )}
                <span className="text-text-muted">or pick one:</span>
                <AccountSitePicker
                  accounts={allAccounts}
                  value=""
                  onChange={(v) => v && setAccountId(v)}
                  style={{ ...INPUT, width: 240 }}
                  placeholder="Choose an account or site..."
                />
              </div>
            </>
          )}
        </section>

        {/* CONTACTS */}
        {M.cards.contacts && people.length > 0 && (
          <section className={CARD} aria-label="Contacts">
            <div className="font-bold text-text-dark">
              {num('contacts')}. Contacts ({people.length})
            </div>
            {people.map((p, i) => (
              <div key={i} className="border-t border-border pt-2.5 first-of-type:border-0 space-y-2">
                {p.match && p.match.status === 'matched' && (
                  <div className="text-amber-800">
                    Already on file: {p.match.candidates.map((c) => `${c.name}${c.accountName ? ` at ${c.accountName}` : ''}`).join('; ')}
                  </div>
                )}
                {p.added ? (
                  <div className="text-gat-green font-semibold">
                    {p.form.name} added to {p.added}.
                  </div>
                ) : (
                  <>
                    <ContactFields
                      form={p.form}
                      setForm={(fn) =>
                        setPeople((cur) =>
                          cur.map((x, j) => (j === i ? { ...x, form: typeof fn === 'function' ? fn(x.form) : fn, err: '' } : x))
                        )
                      }
                      primaryLabel="Primary contact for this account / site"
                    />
                    {rights.contact && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" disabled={!account || p.busy} onClick={() => addContact(i)}>
                          {p.busy ? 'Adding...' : p.match && p.match.status === 'matched' ? 'Add as a new contact' : 'Add contact'}
                        </Button>
                        {!account && <span className="text-text-muted">{WAIT_FOR_ACCOUNT}</span>}
                      </div>
                    )}
                    {p.err && (
                      <div role="alert" className="text-status-lost">
                        {p.err}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </section>
        )}

        {/* OPPORTUNITY */}
        {M.cards.opportunity && opp && (
          <section className={CARD} aria-label="Opportunity">
            <div className="flex items-center">
              <span className="font-bold text-text-dark">{num('opportunity')}. Opportunity</span>
              {done.opportunity && <Chip tone="done">Created</Chip>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name" value={opp.topic} onChange={(v) => setOpp((o) => ({ ...o, topic: v }))} wide />
              <Field label="Description" value={opp.description} onChange={(v) => setOpp((o) => ({ ...o, description: v }))} area wide />
              <Field
                label="Customer RFQ / project #"
                value={opp.customerReference}
                onChange={(v) => setOpp((o) => ({ ...o, customerReference: v }))}
              />
              <Field label="Quote due" type="date" value={opp.dueDate} onChange={(v) => setOpp((o) => ({ ...o, dueDate: v }))} />
              <Field label="Value stated by the customer" value={opp.value} onChange={(v) => setOpp((o) => ({ ...o, value: v }))} />
            </div>
            {rights.opportunity ? (
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={!account} onClick={() => setSub('opportunity')}>
                  {done.opportunity ? 'Create another' : 'Create opportunity'}
                </Button>
                {!account && <span className="text-text-muted">{WAIT_FOR_ACCOUNT}</span>}
              </div>
            ) : (
              <span className="text-text-muted">Creating an opportunity needs Edit on Opportunities.</span>
            )}
          </section>
        )}

        {/* ACTIVITY */}
        {M.cards.activity && act && (
          <section className={CARD} aria-label="Activity">
            <div className="flex items-center">
              <span className="font-bold text-text-dark">{num('activity')}. Activity</span>
              {done.activity && <Chip tone="done">Logged</Chip>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span style={LABEL}>Type</span>
                <select
                  style={{ ...INPUT, appearance: 'auto' }}
                  value={act.type}
                  onChange={(e) => setAct((a) => ({ ...a, type: e.target.value }))}
                >
                  <option value="">-</option>
                  {MEETING_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <Field label="Date" type="date" value={act.date} onChange={(v) => setAct((a) => ({ ...a, date: v }))} />
              <Field label="Subject" value={act.subject} onChange={(v) => setAct((a) => ({ ...a, subject: v }))} wide />
              <Field label="Notes" value={act.text} onChange={(v) => setAct((a) => ({ ...a, text: v }))} area wide />
            </div>
            {files.length > 0 && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} />
                Attach the {files.length === 1 ? 'file' : `${files.length} files`} to the activity
              </label>
            )}
            {rights.activity ? (
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={!account || done.activity} onClick={() => setSub('activity')}>
                  Log activity
                </Button>
                {!account && <span className="text-text-muted">{WAIT_FOR_ACCOUNT}</span>}
              </div>
            ) : (
              <span className="text-text-muted">Logging an activity needs Edit on Accounts.</span>
            )}
          </section>
        )}
        <div className="text-text-muted">Nothing is saved until you press Save in each form.</div>
      </div>
    );
  } else {
    /* ── step 1 ── */
    body = (
      <div className="space-y-3 text-[12px]">
        <div className="text-text-body">{M.intro}</div>
        <label className="block">
          <span className="font-semibold text-text-dark">Text</span>
          <textarea
            aria-label="Text to read"
            className="mt-1 w-full border border-border-strong rounded px-2.5 py-1.5 min-h-[120px]"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the email, signature or RFQ here"
          />
        </label>
        <div
          onDragOver={(e) => {
            acceptDrag(e);
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            add(filesOf(e.dataTransfer));
          }}
          className={clsx(
            'border-2 border-dashed rounded p-3 text-center text-text-body',
            drag ? 'border-gat-green bg-gat-green/5' : 'border-border-strong'
          )}
        >
          <div>Drag screenshots here, paste one (Ctrl+V), or</div>
          <div className="mt-2 flex justify-center">
            <Button size="sm" variant="secondary" onClick={() => pick.current?.click()}>
              Choose files
            </Button>
          </div>
          <input
            ref={pick}
            type="file"
            multiple
            hidden
            accept={ACCEPT}
            aria-label="Choose files"
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
          <div className="mt-1 text-text-muted">
            PNG, JPG, GIF, WebP or PDF - up to {MAX_FILES} files, {MAX_TOTAL_MB} MB together.
          </div>
        </div>
        {files.length > 0 && (
          <ul className="space-y-1.5">
            {files.map((f, i) => (
              <li key={`${f.file.name}-${i}`} className="flex items-center gap-2">
                {f.url ? (
                  <img src={f.url} alt="" className="w-12 h-9 object-cover rounded border border-border-strong" />
                ) : (
                  <span className="w-12 h-9 rounded border border-border-strong flex items-center justify-center text-[10px]">PDF</span>
                )}
                <span className="flex-1 truncate">{f.file.name}</span>
                <span className="text-text-muted">{(f.file.size / 1024 / 1024).toFixed(1)} MB</span>
                <button
                  type="button"
                  aria-label={`Remove ${f.file.name}`}
                  className="px-1.5 text-text-muted hover:text-status-lost"
                  onClick={() =>
                    setFiles((cur) => {
                      dropUrl(cur[i].url);
                      return cur.filter((_, j) => j !== i);
                    })
                  }
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        {totalMb > MAX_TOTAL_MB && (
          <div className="text-status-lost">
            Files add up to {totalMb.toFixed(1)} MB - the limit is {MAX_TOTAL_MB} MB.
          </div>
        )}
        {msg && (
          <div role="status" className={msg.tone === 'err' ? 'text-status-lost' : 'text-text-dark font-semibold'}>
            {msg.text}
          </div>
        )}
      </div>
    );
  }

  const footer = sub ? null : found ? (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setFound(null);
          setMsg(null);
        }}
      >
        Back to the text
      </Button>
      <Button onClick={onClose}>Done</Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button onClick={read} disabled={!canRead}>
        {busy ? 'Reading...' : 'Read it'}
      </Button>
    </>
  );

  return (
    <>
      {/* the opportunity editor is its own modal: this one steps aside (keeping its state) while it is open */}
      <Modal open={sub !== 'opportunity'} onClose={close} title={M.title} width={760} footer={footer}>
        {body}
      </Modal>
      {sub === 'opportunity' && account && (
        <EditOpportunityModal
          opp={opportunitySeed(opp, account, allAccounts)}
          onClose={() => setSub(null)}
          onSaved={() => setDone((d) => ({ ...d, opportunity: true }))}
        />
      )}
    </>
  );
}
